import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../../../contexts/auth-context'
import { useVoiceCall } from '../../../contexts/voice-call-context'
import { useVoiceCallSession } from '../../../hooks/use-voice-call-session'
import { getCachedDirectoryUser } from '../../../services/chat-service'
import { buildRecordingConsentContext } from '@shared/calls/recording-consent'

function RecordingIcon() {
  return (
    <svg className="h-6 w-6 text-brand-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" fill="currentColor" stroke="none" />
    </svg>
  )
}

/**
 * Recording/transcription consent notice. Two variants, one notice:
 *
 * - PRE-JOIN (`recordingConsentPending`, phase `incoming`): shown after the
 *   callee taps Accept, before the call connects. Members may opt out —
 *   provider calls require consent to join; declining ends the call.
 *
 * - POST-CONNECT (`recordingConsentOwedPostConnect`, phase `active`): shown the
 *   moment the call connects when the user answered somewhere no notice could
 *   be presented (iOS CallKit lock-screen answer). The call audio keeps
 *   flowing — the sheet only overlays the in-call UI — and recording/
 *   transcription stay server-side deferred until the user confirms here.
 */
export function RecordingConsentOverlay() {
  const session = useVoiceCallSession()
  const { user } = useAuth()
  const {
    acceptCall,
    declineCall,
    declineRecordingConsent,
    cancelRecordingConsentRequest,
    notePostConnectConsentNoticeShown,
    confirmPostConnectRecordingConsent,
    declinePostConnectRecordingConsent,
  } = useVoiceCall()

  const preJoinOpen = Boolean(session?.recordingConsentPending && session.phase === 'incoming')
  const postConnectOpen = Boolean(
    !preJoinOpen && session?.recordingConsentOwedPostConnect && session.phase === 'active',
  )
  const open = preJoinOpen || postConnectOpen
  const variant: 'pre-join' | 'post-connect' = postConnectOpen ? 'post-connect' : 'pre-join'

  // Tap-through guard: the modal can open directly under the finger that just
  // tapped (Accept on the incoming screen, or any in-call control at the
  // connect moment), so the second half of a double-tap would land on the
  // consent/decline buttons before the user has seen the notice. Keep the
  // decision buttons disabled for a short arming window.
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    if (!open) {
      setArmed(false)
      return
    }
    const timer = setTimeout(() => setArmed(true), 700)
    return () => clearTimeout(timer)
  }, [open, variant])

  // Per-call display telemetry for the post-connect variant — recorded when the
  // sheet GENUINELY renders (the owed flag can be set minutes before the
  // suspended WebView ever paints it).
  useEffect(() => {
    if (postConnectOpen) notePostConnectConsentNoticeShown()
  }, [postConnectOpen, notePostConnectConsentNoticeShown])

  // Focus management: move focus into the dialog on open and trap Tab inside it
  // (the in-call UI below stays interactive for a screen reader otherwise).
  const dialogRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open) return
    const dialog = dialogRef.current
    if (!dialog) return
    dialog.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const focusables = dialog.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }
    dialog.addEventListener('keydown', onKeyDown)
    return () => dialog.removeEventListener('keydown', onKeyDown)
  }, [open, variant])

  const remoteUserId = session?.callerId
  const context = useMemo(() => {
    if (!session || !user || !remoteUserId) return null
    const remote =
      getCachedDirectoryUser(remoteUserId) ??
      (session.peer?.userId === remoteUserId ? { roleType: null as string | null } : null)
    return buildRecordingConsentContext(
      { roleType: user.roleType },
      remote ? { roleType: remote.roleType } : null,
      session.mediaType ?? 'voice',
      variant,
    )
  }, [session, user, remoteUserId, variant])

  if (!open || !session || !context) return null

  const peerName = session.peer?.displayName?.trim() || 'Caller'
  const callKind = session.mediaType === 'video' ? 'video' : 'voice'

  return (
    <div
      className="fixed inset-0 z-[130] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recording-consent-title"
      aria-describedby="recording-consent-body"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl outline-none sm:p-6"
      >
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-50">
            <RecordingIcon />
          </div>
          <div className="min-w-0 flex-1">
            <h2 id="recording-consent-title" className="text-base font-semibold text-slate-900">
              {context.title}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {variant === 'post-connect' ? (
                <>
                  You&apos;re on a {callKind} call with{' '}
                  <span className="font-medium text-slate-800">{peerName}</span>
                </>
              ) : (
                <>
                  Incoming {callKind} call from{' '}
                  <span className="font-medium text-slate-800">{peerName}</span>
                </>
              )}
            </p>
          </div>
        </div>

        <p id="recording-consent-body" className="mt-4 text-sm leading-relaxed text-slate-700">
          {context.body}
        </p>

        {context.isProviderCall && context.canOptOut && (
          <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
            {variant === 'post-connect'
              ? 'Provider calls must be recorded. If you opt out, this call will end.'
              : 'Provider calls must be recorded. If you opt out, you will not join this call.'}
          </p>
        )}

        <div className="mt-5 flex flex-col gap-2">
          <button
            type="button"
            disabled={!armed}
            onClick={() =>
              void (variant === 'post-connect'
                ? confirmPostConnectRecordingConsent()
                : acceptCall({ consentNoticeConfirmed: true }))
            }
            className="a11y-touch-target w-full rounded-xl bg-emerald-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:cursor-default disabled:opacity-60"
          >
            {context.acceptLabel}
          </button>
          <button
            type="button"
            disabled={!armed}
            onClick={() =>
              void (variant === 'post-connect'
                ? declinePostConnectRecordingConsent()
                : context.canOptOut
                  ? declineRecordingConsent()
                  : declineCall())
            }
            className="a11y-touch-target w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 disabled:cursor-default disabled:opacity-60"
          >
            {context.declineLabel}
          </button>
          {variant === 'pre-join' && (
            <button
              type="button"
              onClick={() => void cancelRecordingConsentRequest()}
              className="a11y-touch-target w-full rounded-xl px-4 py-2 text-sm font-medium text-slate-500 transition hover:text-slate-700"
            >
              Go back
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
